/**
 * EntityFormHeader component.
 *
 * Renders the header area of an EntityForm, including an optional title,
 * description, and custom children content. Text values are resolved
 * with a two-tier priority:
 *
 * 1. Props (`title` / `description`) take highest precedence.
 * 2. Config i18n overrides (`config.i18n?.title` / `config.i18n?.description`).
 * 3. Omitted entirely when neither source provides a value.
 *
 * @see Spec section 3.3 for the EntityFormHeader contract.
 */

import * as React from 'react';
import { Text } from '@components/base/display/Text/Text';
import { useEntityFormContext } from '../context/EntityFormContext';
import type { EntityFormHeaderProps } from '../types';

// ---------------------------------------------------------------------------
// Text Resolution Helpers
// ---------------------------------------------------------------------------

/**
 * Resolves the title string from props or config i18n overrides.
 *
 * @param propsTitle  - Title provided directly via component props.
 * @param configTitle - Title from `config.i18n?.title`.
 * @returns The resolved title, or `undefined` when neither source provides one.
 */
function resolveTitle(
  propsTitle: string | undefined,
  configTitle: string | undefined,
): string | undefined {
  return propsTitle ?? configTitle;
}

/**
 * Resolves the description string from props or config i18n overrides.
 *
 * @param propsDescription  - Description provided directly via component props.
 * @param configDescription - Description from `config.i18n?.description`.
 * @returns The resolved description, or `undefined` when neither source provides one.
 */
function resolveDescription(
  propsDescription: string | undefined,
  configDescription: string | undefined,
): string | undefined {
  return propsDescription ?? configDescription;
}

// ---------------------------------------------------------------------------
// EntityFormHeader Component
// ---------------------------------------------------------------------------

/**
 * Header compound component for EntityForm.
 *
 * Renders an `<h2>` title and a muted description when resolved values
 * are available, followed by any custom children content.
 *
 * Must be rendered inside an `<EntityForm>` provider.
 *
 * @example
 * ```tsx
 * <EntityForm config={config}>
 *   <EntityForm.Header title="Create User" description="Fill in the details below." />
 *   {/* ... *\/}
 * </EntityForm>
 * ```
 *
 * @example
 * ```tsx
 * // Title and description from config.i18n
 * <EntityForm config={configWithI18n}>
 *   <EntityForm.Header />
 *   {/* ... *\/}
 * </EntityForm>
 * ```
 *
 * @example
 * ```tsx
 * // Custom children content
 * <EntityForm config={config}>
 *   <EntityForm.Header title="Edit User">
 *     <Badge variant="info">Draft</Badge>
 *   </EntityForm.Header>
 * </EntityForm>
 * ```
 */
export function EntityFormHeader({
  title: propsTitle,
  description: propsDescription,
  children,
  className,
}: Readonly<EntityFormHeaderProps>): React.ReactElement | null {
  const { config } = useEntityFormContext();

  // --- Resolve text values ---

  const title = resolveTitle(propsTitle, config.i18n?.title);
  const description = resolveDescription(propsDescription, config.i18n?.description);

  // --- Bail out if there is nothing to render ---

  const hasContent = title !== undefined || description !== undefined || children !== undefined;
  if (!hasContent) {
    return null;
  }

  // --- Build class name ---

  const headerClassName = ['space-y-1 mb-6', config.cssOverrides?.header, className]
    .filter(Boolean)
    .join(' ');

  // --- Render ---

  return (
    <div className={headerClassName} data-testid="entity-form-header">
      {title !== undefined && (
        <Text as="h2" size="xl" weight="semibold">
          {title}
        </Text>
      )}
      {description !== undefined && (
        <Text color="muted" size="sm">
          {description}
        </Text>
      )}
      {children}
    </div>
  );
}

EntityFormHeader.displayName = 'EntityFormHeader';
